import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmationModal } from "../elements/ConfirmationModal";
import { Button } from "../elements/Button";
import { DeleteIcon, RotateIcon } from "../icons";
import { useGlobalReducer } from "../../GlobalContext";
import { TabbedTables } from "../blocks/layout/TabbedTables";
import { RelatedTableConfig } from "../blocks/layout/TabbedTables.types";
import { tabConfigToTableConfig } from "../blocks/layout/tabConfigToTableConfig";
import { TableTabConfig } from "../config/tableConfig.types";
import { usePageState } from "../context/usePageState";
import "../lists/ListRowActions.scss";
import { getRelatedTableActions } from "./relatedTableActions";
import {
  RelatedRow,
  RelatedTableActions,
  RelatedTableRemoveAction,
  RelatedTableRowAction,
} from "./relatedTableActions.types";
import { RelatedEntity } from "./relatedEntity.types";
import { useEntityPage } from "./useEntityPage";

interface PendingRemoval {
  entityName: string;
  row: RelatedRow;
  action: RelatedTableRemoveAction;
}

interface PendingRowAction {
  entityName: string;
  row: RelatedRow;
  action: RelatedTableRowAction;
}

export interface RelatedTablesProps {
  tabs: TableTabConfig[];
  title?: string;
}

/**
 * A page's related tables, with the add and remove actions registered for them. The
 * actions are the page's rather than the view's, so they appear whichever view the
 * record is on; the designer draws the bare tables.
 */
export function RelatedTables({ tabs, title }: RelatedTablesProps) {
  const { entityName, record, isNew, onRelatedRecordsChange } = useEntityPage();
  const { hasAnyRole } = usePageState();
  const globalDispatch = useGlobalReducer();
  const [pending, setPending] = useState<PendingRemoval | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingRowAction | null>(null);
  const [running, setRunning] = useState<{ rowId: unknown; actionId: string } | null>(
    null
  );
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const navigate = useNavigate();

  const parentId = String(record?.id ?? "");
  const relatedEntities = (record?.relatedEntities ?? []) as RelatedEntity[];
  const recordsOf = (name: string) =>
    relatedEntities.find((related) => related.entityName === name)
      ?.entities ?? [];

  const tables = tabConfigToTableConfig(tabs).map(
    (table): RelatedTableConfig => {
      const actions = getRelatedTableActions(entityName, table.entityName);

      // A new record has nothing to refresh, add to or act on yet.
      if (isNew || !actions || (actions.roles && !hasAnyRole(actions.roles))) {
        return table;
      }

      const { add: AddAction, remove, rowActions = [], refresh } = actions;
      const offeredOn = (row: RelatedRow) =>
        rowActions.filter((action) => !action.visible || action.visible(row));

      return {
        ...table,
        ...(remove || rowActions.length > 0
          ? {
              rowActions: (row: RelatedRow) =>
                !remove && offeredOn(row).length === 0 ? null : (
                <div className="d-flex justify-content-end align-items-center gap-1">
                  {offeredOn(row).map((action) => (
                    <Button
                      key={action.id}
                      text={action.label}
                      variant={action.variant}
                      className="btn-sm"
                      disabled={running !== null}
                      showSpinner={
                        running?.rowId === row.id && running?.actionId === action.id
                      }
                      onClick={() => startRowAction(table.entityName, row, action)}
                    />
                  ))}
                  {remove && (
                  <button
                    type="button"
                    className="list-row-action hover-text-danger"
                    title={remove.label(row)}
                    aria-label={remove.label(row)}
                    disabled={isRemoving}
                    onClick={() =>
                      setPending({
                        entityName: table.entityName,
                        row,
                        action: remove,
                      })
                    }
                  >
                    <FontAwesomeIcon icon={DeleteIcon} />
                  </button>
                  )}
                </div>
              ),
            }
          : {}),
        ...(AddAction || refresh
          ? {
              toolbarActions: (
                <div className="d-flex align-items-center gap-2">
                  {refresh && (
                    <Button
                      title="Refresh"
                      icon={RotateIcon}
                      variant="link"
                      className="px-2 py-1 rounded-2 bg-white-hover"
                      disabled={refreshing !== null}
                      showSpinner={refreshing === table.entityName}
                      onClick={() => refreshTable(table.entityName, refresh)}
                    />
                  )}
                  {AddAction && (
                    <AddAction
                      parentId={parentId}
                      records={recordsOf(table.entityName)}
                      onAdded={(records) =>
                        onRelatedRecordsChange(table.entityName, records)
                      }
                    />
                  )}
                </div>
              ),
            }
          : {}),
      };
    }
  );

  const refreshTable = async (
    entityName: string,
    refresh: NonNullable<RelatedTableActions["refresh"]>
  ) => {
    setRefreshing(entityName);

    try {
      const records = await refresh({ globalDispatch, parentId });

      if (records) {
        onRelatedRecordsChange(entityName, records);
      }
    } catch {
      // The service has already said what went wrong; the rows stay as they were.
    } finally {
      setRefreshing(null);
    }
  };

  // Replaces the table's rows with what the action hands back, like a removal does, so
  // edits not yet saved on the record survive it.
  const runRowAction = async (
    entityName: string,
    row: RelatedRow,
    action: RelatedTableRowAction
  ) => {
    setRunning({ rowId: row.id, actionId: action.id });

    try {
      const records = await action.run({ globalDispatch, parentId, row, navigate });

      if (records) {
        onRelatedRecordsChange(entityName, records);
      }
    } catch {
      // The service has already said what went wrong; the rows stay as they were.
    } finally {
      setRunning(null);
      setPendingAction(null);
    }
  };

  const startRowAction = (
    entityName: string,
    row: RelatedRow,
    action: RelatedTableRowAction
  ) => {
    if (action.confirmation) {
      setPendingAction({ entityName, row, action });
    } else {
      runRowAction(entityName, row, action);
    }
  };

  const actionConfirmation = pendingAction?.action.confirmation?.(pendingAction.row);

  // The row goes from the table once the service has taken it off, without re-reading
  // the whole record - which would also throw away any edits not yet saved.
  const confirmRemoval = async () => {
    if (!pending) {
      return;
    }

    setIsRemoving(true);

    try {
      const removed = await pending.action.remove(
        globalDispatch,
        parentId,
        pending.row,
        record ?? {}
      );

      if (removed) {
        onRelatedRecordsChange(
          pending.entityName,
          recordsOf(pending.entityName).filter(
            (related) => related.Id !== pending.row.id
          )
        );
        setPending(null);
      }
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmation = pending?.action.confirmation(pending.row);

  const newRecordMessage = isNew && tabs.length === 1 ? tabs[0].newRecordMessage : undefined;

  return (
    <>
      {newRecordMessage ? (
        <section>
          <h2 className="page-section__title fw-semibold h5 mb-3">{tabs[0].label}</h2>
          <p className="text-body-secondary mb-0">{newRecordMessage}</p>
        </section>
      ) : (
        <TabbedTables
          tables={tables}
          relatedEntities={relatedEntities}
          title={title}
        />
      )}
      {pending && confirmation && (
        <ConfirmationModal
          modalTitle={confirmation.title}
          confirmText={confirmation.confirmText}
          isSubmitting={isRemoving}
          onClose={() => setPending(null)}
          onConfirm={confirmRemoval}
        >
          {confirmation.message}
        </ConfirmationModal>
      )}
      {pendingAction && actionConfirmation && (
        <ConfirmationModal
          modalTitle={actionConfirmation.title}
          confirmText={actionConfirmation.confirmText}
          isSubmitting={running !== null}
          onClose={() => setPendingAction(null)}
          onConfirm={() =>
            runRowAction(pendingAction.entityName, pendingAction.row, pendingAction.action)
          }
        >
          {actionConfirmation.message}
        </ConfirmationModal>
      )}
    </>
  );
}

export default RelatedTables;
